/**
 * Cookie utility functions for client-side cookie management
 */

import { useEffect, useMemo, useState } from 'react';

/**
 * Get a cookie value by name
 * @param name - The name of the cookie
 * @returns The cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null;
  }

  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

/** Get cookie as a hook.  Returns undefined before cookies are ready, and null if the cookie is not found
 * @param cookieName - The name of the cookie
 * @returns The cookie value or null if not found
 * @example
 * const profileId = useCookie('gmi.profile.id');
 * if (profileId) {
 *   console.log('Profile ID:', profileId);
 * }
 */
export const useCookie = (cookieName: string): string | null | 'loading' => {
  const [cookie, setCookie] = useState<string | null | 'loading'>('loading');

  const onCookieChange = useMemo(
    () => (ev: CookieChangeEvent) => {
      const { changed, deleted } = ev;
      for (const { name, value } of changed) {
        if (name === cookieName) {
          setCookie(value ?? null);
        }
      }

      for (const { name } of deleted) {
        if (name === cookieName) {
          setCookie(null);
        }
      }
    },
    [cookieName]
  );

  useEffect(() => {
    setCookie(getCookie(cookieName));

    // Feature detection: Check if cookieStore API is available
    if (typeof window !== 'undefined' && 'cookieStore' in window) {
      window.cookieStore.addEventListener('change', onCookieChange);
      return () => {
        window.cookieStore.removeEventListener('change', onCookieChange);
      };
    } else {
      // Fallback for iOS/Safari: Poll for cookie changes
      const intervalId = setInterval(() => {
        const currentValue = getCookie(cookieName);
        setCookie((prevValue) => {
          // Only update if value actually changed (avoid unnecessary re-renders)
          if (prevValue !== 'loading' && prevValue !== currentValue) {
            return currentValue;
          }
          return prevValue;
        });
      }, 1000); // Check every second

      return () => clearInterval(intervalId);
    }
  }, [cookieName, onCookieChange]);
  return cookie;
};

/**
 * Set a cookie with optional expiration
 * @param name - The name of the cookie
 * @param value - The value to store
 * @param days - Number of days until expiration (default: session cookie)
 */
export const setCookie = (name: string, value: string, days?: number) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  // added secure and SameSite attributes for better security
  let cookieString = `${name}=${encodeURIComponent(value)}; path=/; Secure; SameSite=Lax`;

  if (days !== undefined) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    cookieString += `; expires=${expires}`;
  }

  document.cookie = cookieString;
};

/**
 * Remove a cookie by setting it to expire immediately
 * @param name - The name of the cookie to remove
 */
export const removeCookie = (name: string) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

// Typescript doesn't support the CookieChangeEvent interface, so we need to define it ourselves.

/**
 * The **`CookieChangeEvent`** interface of the Cookie Store API is the event type of the CookieStore/change_event event fired at a CookieStore when any cookies are created or deleted.
 * Available only in secure contexts.
 *
 * [MDN Reference](https://developer.mozilla.org/docs/Web/API/CookieChangeEvent)
 */
export interface CookieChangeEvent extends Event {
  /**
   * The **`changed`** read-only property of the CookieChangeEvent interface returns an array of the cookies that have been changed.
   *
   * [MDN Reference](https://developer.mozilla.org/docs/Web/API/CookieChangeEvent/changed)
   */
  readonly changed: ReadonlyArray<CookieListItem>;
  /**
   * The **`deleted`** read-only property of the CookieChangeEvent interface returns an array of the cookies that have been deleted by the given `CookieChangeEvent` instance.
   *
   * [MDN Reference](https://developer.mozilla.org/docs/Web/API/CookieChangeEvent/deleted)
   */
  readonly deleted: ReadonlyArray<CookieListItem>;
}

export interface CookieListItem {
  name?: string;
  value?: string;
}
